/**
 * CorrelatedRow — canonical Zod schema for /api/v4/admin/dual-write/rows.
 *
 * Used by the admin dual-write diagnostics UI to show legacy ↔ V4 artifact
 * correlation. The backend assembler selects exactly these fields from
 * `terra_ingestion` joined with `v4_artifacts`. The schema enforces the
 * shape at the wire boundary (via validateResponse) so the UI can render
 * typed fields without unsafe casts.
 *
 * Notes on shape tightening:
 * - `v4.blueprint` is a parsed subset of the Blueprint row (displayName,
 *   kind, actions/card/detail/artifactCard as JSON blobs). The UI currently
 *   only reads `displayName`, but the full blob is exposed because the
 *   detail panel renders it as syntax-highlighted JSON.
 * - `v4.salesforceObjectId` is a first-class column on `V4Artifact` and is
 *   now emitted by the assembler (previously the UI tried to read it out of
 *   `data`, which never contained it).
 * - `v4.outcome` has been removed: the `outcome` column was dropped from
 *   `V4Artifact` in commit 028355a3 (drop artifact outcome column).
 */

import { z } from 'zod';
import { JsonValueSchema } from '../schemas/json-value';

const IsoTimestampSchema = z.union([z.string(), z.date().transform((d) => d.toISOString())]);

export const CorrelatedRowHttpLogSchema = z.object({
  id: z.number(),
  timestamp: IsoTimestampSchema,
  method: z.string(),
  path: z.string(),
  statusCode: z.number().nullable(),
  durationMs: z.number().nullable(),
  body: JsonValueSchema,
  headers: JsonValueSchema,
  error: z.string().nullable(),
});

export const CorrelatedRowV4BlueprintSchema = z.object({
  kind: z.string(),
  displayName: z.string(),
  actions: JsonValueSchema,
  card: JsonValueSchema,
  detail: JsonValueSchema.nullable(),
  artifactCard: z.string().nullable(),
});

export const CorrelatedRowV4Schema = z.object({
  uuid: z.string(),
  kind: z.string(),
  externalId: z.string(),
  createdAt: IsoTimestampSchema,
  read: z.boolean(),
  salesforceObjectId: z.string().nullable(),
  data: JsonValueSchema,
  rawPayload: JsonValueSchema,
  blueprint: CorrelatedRowV4BlueprintSchema.nullable(),
  httpLog: z
    .object({ id: z.number(), body: JsonValueSchema })
    .nullable(),
});

export const CorrelatedRowSchema = z.object({
  legacyId: z.number(),
  externalId: z.string().nullable(),
  artifactName: z.string().nullable(),
  accountName: z.string().nullable(),
  status: z.string().nullable(),
  category: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  ownerSlackId: z.string().nullable(),
  objectId: z.string().nullable(),
  timestamp: IsoTimestampSchema.nullable(),
  summary: z.string().nullable(),
  content: JsonValueSchema,
  envelope: JsonValueSchema,
  rawRequestBody: JsonValueSchema,
  httpLog: CorrelatedRowHttpLogSchema.nullable(),
  v4: CorrelatedRowV4Schema.nullable(),
});

export const RowsResponseSchema = z.object({
  rows: z.array(CorrelatedRowSchema),
  hasMore: z.boolean(),
});

export type CorrelatedRow = z.infer<typeof CorrelatedRowSchema>;
export type CorrelatedRowV4 = z.infer<typeof CorrelatedRowV4Schema>;
export type CorrelatedRowV4Blueprint = z.infer<typeof CorrelatedRowV4BlueprintSchema>;
export type CorrelatedRowHttpLog = z.infer<typeof CorrelatedRowHttpLogSchema>;
export type RowsResponse = z.infer<typeof RowsResponseSchema>;
